import { createFileRoute, Outlet, redirect, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { LayoutDashboard, Building2, Users, Layers, Megaphone, FileText, ShieldCheck, LogOut, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsSuperAdmin } from "@/hooks/use-is-super-admin";

export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  beforeLoad: async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) throw redirect({ to: "/auth" });
    return { user: data.user };
  },
  component: AppShell,
});

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/organizations", label: "Organizations", icon: Building2 },
  { to: "/contacts", label: "Contacts", icon: Users },
  { to: "/segments", label: "Segments", icon: Layers },
  { to: "/templates", label: "Templates", icon: FileText },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/audit", label: "Audit log", icon: ShieldCheck },
] as const;


function AppShell() {
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { isAdmin } = useIsSuperAdmin();

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <aside className="hidden md:flex w-60 flex-col border-r border-border bg-sidebar p-4 gap-1">
        <div className="px-2 py-3 mb-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Campaign</div>
          <div className="text-lg font-semibold">Messaging Platform</div>
        </div>
        {NAV.map((n) => {
          const active = path === n.to || path.startsWith(n.to + "/");
          return (
            <Link
              key={n.to}
              to={n.to}
              className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                active ? "bg-primary text-primary-foreground" : "text-sidebar-foreground hover:bg-sidebar-accent"
              }`}
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </Link>
          );
        })}
        {isAdmin && (
          <Link to="/admin"
            className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors mt-2 border-t border-border pt-3 ${
              path === "/admin" || path.startsWith("/admin/")
                ? "bg-primary text-primary-foreground"
                : "text-sidebar-foreground hover:bg-sidebar-accent"
            }`}>
            <Crown className="h-4 w-4" />
            Super admin
          </Link>
        )}
        <div className="mt-auto">
          <Button variant="ghost" className="w-full justify-start" onClick={signOut}>
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
